import { useState, useEffect } from "react";
import MiniCupBanner from "../../components/football/MiniCupBanner";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, History, CheckCircle2, XCircle, AlertTriangle, MinusCircle } from "lucide-react";
import { useFb } from "../../utils/football";

const fmtDay = (md) => {
  try { return new Date(md + "T00:00:00Z").toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" }); }
  catch { return md; }
};

const StatusIcon = ({ row }) => {
  if (!row.resolved) return <MinusCircle size={15} className="text-muted-foreground" />;
  if (!row.answered) return <AlertTriangle size={15} className="text-orange-400" />;
  return row.is_correct ? <CheckCircle2 size={15} className="text-emerald-400" /> : <XCircle size={15} className="text-red-400" />;
};

const HistoryRow = ({ row }) => (
  <div className="rounded-xl border border-border bg-card p-3.5" data-testid="history-row">
    <div className="flex items-start justify-between gap-2">
      <span className="text-sm font-bold">{row.question_text}</span>
      <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{fmtDay(row.match_day)}</span>
    </div>
    <div className="mt-2 flex items-center gap-1.5 text-xs">
      <StatusIcon row={row} />
      {!row.answered ? (
        <span className="text-orange-300">Not answered{row.resolved && row.correct_answer ? <> · Correct: <b className="text-foreground">{row.correct_answer}</b></> : null}</span>
      ) : (
        <span className="text-muted-foreground">
          Your answer: <b className="text-foreground">{row.user_answer}</b>
          {row.resolved && <> · Correct: <b className="text-foreground">{row.correct_answer ?? "—"}</b> · {row.points_earned || 0} pts</>}
          {!row.resolved && <> · <span className="wc-gold">Pending</span></>}
        </span>
      )}
    </div>
  </div>
);

export default function FootballPredictorHistoryPage() {
  const fb = useFb();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const d = await fb.get("/football/predictor/my-history", { days: 30 });
        setRows(d.history || []);
      } catch { setRows([]); }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="football-theme min-h-screen bg-background pb-12" data-testid="predictor-history-page">
      <div className="px-4 pt-3"><MiniCupBanner /></div>
      <div className="wc-hero-gradient px-4 pb-5 pt-4">
        <button onClick={() => navigate("/football/leaderboard")} className="mb-2 inline-flex items-center gap-1 text-sm text-white/80" data-testid="history-back"><ChevronLeft size={18} /> Back</button>
        <div className="flex items-center gap-2"><History size={20} className="text-amber-300" /><h1 className="text-xl font-extrabold text-white">My Predictor History</h1></div>
        <p className="mt-1 text-xs text-white/70">Last 30 days · track what you predicted and missed.</p>
      </div>
      <div className="mx-auto max-w-md space-y-3 px-4 pt-4">
        {loading ? <div className="h-40 animate-pulse rounded-xl bg-secondary/50" /> :
          rows.length === 0 ? <div className="mt-10 text-center text-sm text-muted-foreground" data-testid="history-empty">No predictor questions in the last 30 days.</div> :
          rows.map((row) => <HistoryRow key={row.question_id} row={row} />)}
      </div>
    </div>
  );
}
